import { Inject, Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { ISavedContactDtoModel } from "../models/saved-contact/dto/saved-contact-dto.model";
import { SavedContactMapping } from "../mappings/saved-contact.mapping";
import { ISavedContactModel } from "../models/saved-contact/saved-contact.model";
import { IPaginationResponseModel } from "../models/generic/pagination/pagination-response.base.model";
import { EnvironmentService } from "./environment.service";
import { ApplicationContextService } from "./application-context.service";
import { CoreService } from './customer-core.service';
import { ISavedAddressModel } from '../models/saved-address/saved-address.model'

@Injectable({
   providedIn: 'root'
})
export class SavedContactsService {
   private mappingResult: SavedContactMapping;
   constructor(private readonly httpClient: HttpClient, 
               public envService: EnvironmentService, 
               private applicationContext: ApplicationContextService,
               private customerService: CoreService) {
      this.mappingResult = new SavedContactMapping();
   }
   
   private path() {
      if (this.applicationContext.isPublic()) {
         return `${this.envService.settings.publicApiUrl}/Customers/contacts`;
      }

      return `${this.envService.settings.apiUrl}/Contacts`;
   }

   public delete(contactId: string, customerId: string): Observable<boolean> {
      const httpOptions = {
         headers: new HttpHeaders({ 'Content-Type': 'application/json' }), body: {
            contactIds: [contactId],
            customerId: this.customerService.getCustomerId(customerId)
         }
      };
      return this.httpClient.delete<boolean>(`${this.path()}?customerId=${this.customerService.getCustomerId(customerId)}`, httpOptions);
   }

   public get(contactId: string, customerId: string): Observable<ISavedContactModel> {
      return this.httpClient.get<ISavedContactDtoModel>(`${this.path()}/${contactId}?customerId=${this.customerService.getCustomerId(customerId)}`).pipe(
         map(dto => this.mappingResult.toApplicationModel(dto))
      );
   }

   public save(model: ISavedContactModel, customerId: string): Observable<any> {
      const dto = this.mappingResult.toPostModel(model, customerId);
      return this.httpClient.post<any>(`${this.path()}?customerId=${this.customerService.getCustomerId(customerId)}`, dto);
   }

   public update(model: ISavedContactModel, customerId: string): Observable<ISavedContactModel> {
      const dto = this.mappingResult.toPutModel(model, customerId);
      return this.httpClient.put<ISavedContactDtoModel>(`${this.path()}/${model.id}?customerId=${this.customerService.getCustomerId(customerId)}`, dto).pipe(
         map(dto => this.mappingResult.toApplicationModel(dto))
      );
   }

   public getAll(customerId: string): Observable<IPaginationResponseModel<ISavedContactModel>> {
      
      return this.httpClient.get<ISavedContactDtoModel[]>(`${this.path()}?customerId=${this.customerService.getCustomerId(customerId)}`).pipe(
         map(dtos => dtos.map(dto => this.mappingResult.toApplicationModel(dto))),
         map(models => {
            return {
               total: models.length,
               data: models
            } as IPaginationResponseModel<ISavedContactModel>
         })
      );
   }

   public defaultContactRequiresAContactNumber(customerId:string):Observable<ISavedContactModel|boolean> {

      try{
         if (this.applicationContext.isPublic() == false) {
            return of(false);
         }

         return this.httpClient.get<ISavedContactDtoModel[]>(`${this.path()}?customerId=${this.customerService.getCustomerId(customerId)}`).pipe(
            map(dtos => dtos.map(dto => this.mappingResult.toApplicationModel(dto))),
            map(models => {

               let defaultContact: ISavedContactModel = models.filter(x => x.isDefault)[0];
            
               if (defaultContact && !defaultContact.telephoneNumber && !defaultContact.mobileNumber) {
                  return defaultContact as ISavedContactModel;
               }else{
                  return false
               }

            })
         );
      }
      catch(e){
         return of(false);
      }

   }

   public applyContactNumberOfAddressToIncompleteDefaultContact(customerId:string, savedAddress:ISavedAddressModel):void {

      if (this.applicationContext.isPublic() == false) {
         return;
      }
   
       try{
 
         this.defaultContactRequiresAContactNumber(customerId)
         .pipe(
            switchMap((defaultContactWithoutContactNumber: ISavedContactModel|boolean) => {

               if(typeof defaultContactWithoutContactNumber != 'boolean'){

                  if(defaultContactWithoutContactNumber.address == null){
                     defaultContactWithoutContactNumber.address = savedAddress;
                  }

                  // add inputted contactNumber to the customers default contact, which we know is empty
                  defaultContactWithoutContactNumber.telephoneNumber = savedAddress.details.contactNumber;

                  return this.update(defaultContactWithoutContactNumber, customerId)

               }else{

                  return of(false)

               }

            })
         )
         .subscribe();
       }
       catch(e){
         //do nothing
       }

   }
}